import * as Yup from 'yup';
import { formatPostalCode, isValidPostalCode } from './PostalCode';
import { areAllEmailsValid } from './emailHelpers';

export const signInValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),
  password: Yup.string()
    // .min(8, "Password must be at least 8 characters")
    .required('Password is required'),
});

export const forgotPasswordValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),
});

export const resetPasswordValidationSchema = Yup.object().shape({
  newPassword: Yup.string().required('New password is required'),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('newPassword')], 'Passwords must match'),
});

export const userAddEditValidation = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  // email: Yup.string()
  //   .email("Invalid email format")
  //   .required("Email is required"),
  phone: Yup.string()
    .required('Phone number is required')
    .max(12, 'Phone number should be less than 12 digits'),
});

export const merchantAddEditValidation = Yup.object().shape({
  // merchantId: Yup.string().required('Merchant Id is required'),
  name: Yup.string().required('Merchant Name is required'),
  priceStrategyId: Yup.mixed().required('Price Strategy is required'),
  // Legal Address fields - conditional validation
  legalAddressLine1: Yup.string()
    .nullable()
    .test(
      'conditional-required',
      'Address Line 1 is required when filling legal address',
      function (value) {
        const {
          legalAddressLine2,
          legalZipCode,
          legalCity,
          legalProvince,
          legalCountry,
        } = this.parent;
        const hasAnyLegalField = [
          legalAddressLine2,
          legalZipCode,
          legalCity,
          legalProvince,
          legalCountry,
        ].some((field) => field && field.trim() !== '');

        if (hasAnyLegalField && (!value || value.trim() === '')) {
          return false;
        }
        return true;
      },
    ),
  legalAddressLine2: Yup.string().nullable(),
  legalZipCode: Yup.string()
    .nullable()
    .test(
      'conditional-required',
      'Zip Code is required when filling legal address',
      function (value) {
        const {
          legalAddressLine1,
          legalAddressLine2,
          legalCity,
          legalProvince,
          legalCountry,
        } = this.parent;
        const hasAnyLegalField = [
          legalAddressLine1,
          legalAddressLine2,
          legalCity,
          legalProvince,
          legalCountry,
        ].some((field) => field && field.trim() !== '');

        if (hasAnyLegalField && (!value || value.trim() === '')) {
          return false;
        }
        return true;
      },
    )
    .test('postal-format', 'Invalid postal code', function (value) {
      if (!value || value.trim() === '') return true;
      const country = this.parent.legalCountry;
      if (isValidPostalCode(formatPostalCode(value, country), country)) {
        return true;
      }
      return this.createError({
        message: `Enter a valid postal code for ${country || 'the selected country'}`,
      });
    }),
  // City is optional
  legalCity: Yup.string().nullable(),
  legalProvince: Yup.string().nullable(),
  legalCountry: Yup.string()
    .nullable()
    .test(
      'conditional-required',
      'Country is required when filling legal address',
      function (value) {
        const {
          legalAddressLine1,
          legalAddressLine2,
          legalZipCode,
          legalCity,
          legalProvince,
        } = this.parent;
        const hasAnyLegalField = [
          legalAddressLine1,
          legalAddressLine2,
          legalZipCode,
          legalCity,
          legalProvince,
        ].some((field) => field && field.trim() !== '');

        if (hasAnyLegalField && (!value || value.trim() === '')) {
          return false;
        }
        return true;
      },
    ),
  commission: Yup.number()
    .typeError('Commission must be a number')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .test('commission-range', 'Commission validation', function (value) {
      if (value === null || value === undefined) return true;
      if (value < 0) {
        return this.createError({
          path: 'commission',
          message: 'Commission cannot be negative',
        });
      }
      if (value > 100) {
        return this.createError({
          path: 'commission',
          message: 'Commission cannot exceed 100%',
        });
      }
      return true;
    }),
  address: Yup.string().required('Address is required'),
  // locationId: Yup.string().required('Location ID is required'),
  // weeklySchedule: Yup.array().of(
  //   Yup.object().shape({
  //     isOpen: Yup.boolean(),
  //     openingTime: Yup.string().test(
  //       'opening-time-validation',
  //       'Invalid opening time',
  //       function (value) {
  //         const { isOpen } = this.parent;
  //         if (!isOpen) {
  //           return value === '00:00';
  //         }
  //         if (!value) return false;
  //         if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(value)) return false;
  //         return true;
  //       },
  //     ),
  //     closingTime: Yup.string().test(
  //       'closing-time-validation',
  //       'Invalid closing time',
  //       function (value) {
  //         const { isOpen, openingTime } = this.parent;
  //         if (!isOpen) {
  //           return value === '00:00';
  //         }
  //         if (!value) return false;
  //         if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(value)) return false;
  //         if (value <= openingTime) return false;
  //         return true;
  //       },
  //     ),
  //   }),
  // ),

  regionId: Yup.mixed()
    .nullable()
    .test('is-required-if-admin', 'Region is required', function (value) {
      const { role } = this.options.context || {};
      const isAdmin = role === 1 || role === '1';
      const regionValue = typeof value === 'object' ? value?.id : value;

      if (isAdmin && !regionValue) {
        return this.createError({
          path: this.path,
          message: 'Region is required',
        });
      }

      return true;
    }),

  merchantEmail: Yup.string()
    .nullable()
    .test(
      'valid-comma-separated-emails',
      'Each email must be a valid email address',
      (value) => areAllEmailsValid(value),
    ),
  financeEmail: Yup.string()
    .nullable()
    .test(
      'valid-comma-separated-finance-emails',
      'Each email must be a valid email address',
      (value) => areAllEmailsValid(value),
    ),
});

export const stationAddEditValidation = Yup.object().shape({
  stationId: Yup.string().required('Station Id is required'),
  slots: Yup.number()
    .typeError('Slots must be a number')
    .required('Slots is required')
    .positive('Slots must be a positive number')
    .integer('Slots must be an integer')
    .min(1, 'Slots must be at least 1')
    .max(100, 'Slots cannot exceed 100'),
  // terminalId: Yup.string().required('Terminal Id is required'),
  // coordinates: Yup.array()
  //     .of(Yup.object().shape({
  //         value: Yup.string().required('Coordinate is required')
  //     }))
  //     .min(1, 'At least one coordinate is required'),
});

export const powerbankIdAddEditValidation = Yup.object().shape({
  powerbankId: Yup.string().required('Powerbank Id is required'),
  slot: Yup.number()
    .typeError('Slots must be a number')
    .required('Slots is required')
    .positive('Slots must be a positive number')
    .integer('Slots must be an integer')
    .min(1, 'Slots must be at least 1')
    .max(100, 'Slots cannot exceed 100'),
  // stationId: Yup.string().required('Station Id is required'),
});

// eslint-disable-next-line no-unused-vars
export const userAdminSchema = (selectedMerchants) =>
  Yup.object().shape({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    roleId: Yup.mixed().required('Role is required'),
    organizationName: Yup.string().test(
      'is-required-organization-name',
      'Organization name is required',
      function (value) {
        const roleId = this.parent.roleId?.id;
        if (roleId === 6 && !value?.trim()) {
          return this.createError({
            path: 'organizationName',
            message: 'Organization name is required.',
          });
        }
        return true;
      },
    ),
    regionId: Yup.mixed().test(
      'is-required-region',
      'Region is required',
      function (value) {
        const roleId = this.parent.roleId?.id;
        if (roleId === 5) {
          if (!value) {
            return this.createError({
              path: 'regionId',
              message: 'Please select region.',
            });
          }
        }
        return true;
      },
    ),
    merchantId: Yup.mixed().test(
      'is-required-merchant',
      'Merchant is required',
      function (value) {
        const roleId = this.parent.roleId?.id;

        if ([4, 6].includes(roleId)) {
          if (!value || (Array.isArray(value) && value.length === 0)) {
            return this.createError({
              path: 'merchantId',
              message: 'Please select merchant.',
            });
          }
        }
        return true;
      },
    ),
    merchantDates: Yup.array().test(
      'merchant-dates-validation',
      'Date validation',
      function (dates) {
        const roleId = this.parent.roleId?.id;
        const merchantIds = this.parent.merchantId;

        // Skip validation if role is not seller-like (Seller/Organization)
        if (![4, 6].includes(roleId)) return true;

        // Skip validation if no merchants
        if (!merchantIds || merchantIds.length === 0) return true;

        // Initialize an object to track all errors
        const errors = [];
        let hasErrors = false;

        // Check if dates array exists and matches merchantIds length
        if (!dates || dates.length !== merchantIds.length) {
          // Create errors for all merchant dates if array is invalid
          for (let i = 0; i < merchantIds.length; i++) {
            errors.push(
              this.createError({
                path: `merchantDates[${i}]`,
                message: 'Date is required',
              }),
            );
          }
          hasErrors = true;
        } else {
          // Check each date individually
          for (let i = 0; i < dates.length; i++) {
            if (!dates[i]) {
              errors.push(
                this.createError({
                  path: `merchantDates[${i}]`,
                  message: 'Date is required',
                }),
              );
              hasErrors = true;
            }
          }
        }

        // Return either true (valid) or combined errors
        return hasErrors ? new Yup.ValidationError(errors) : true;
      },
    ),
    commission: Yup.number()
      .typeError('Commission must be a number')
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      )
      .test('commission-range', 'Commission validation', function (value) {
        const roleId = this.parent.roleId?.id;
        if ([4, 6].includes(roleId) && value !== null && value !== undefined) {
          if (value < 0) {
            return this.createError({
              path: 'commission',
              message: 'Commission cannot be negative',
            });
          }
          if (value > 100) {
            return this.createError({
              path: 'commission',
              message: 'Commission cannot exceed 100%',
            });
          }
        }
        return true;
      }),
    commissionOn: Yup.mixed().test(
      'is-required-commission-on',
      'Commission basis is required',
      function (value) {
        const roleId = this.parent.roleId?.id;
        if ([4, 6].includes(roleId)) {
          if (!value || !value.id) {
            return this.createError({
              path: 'commissionOn',
              message: 'Please select commission basis.',
            });
          }
        }
        return true;
      },
    ),
    // Address fields for seller role (roleId === 4) — all optional
    addressLine1: Yup.string(),
    addressLine2: Yup.string(),
    zipCode: Yup.string().test(
      'postal-format',
      'Invalid postal code',
      function (value) {
        if (!value || value.trim() === '') return true;
        const country = this.parent.country;
        if (isValidPostalCode(formatPostalCode(value, country), country)) {
          return true;
        }
        return this.createError({
          message: `Enter a valid postal code for ${country || 'the selected country'}`,
        });
      },
    ),
    city: Yup.string(),
    province: Yup.string(),
    country: Yup.string(),
    bankAccount: Yup.string(),
    financeEmail: Yup.string().email('Invalid email').nullable(),
  });

export const editProfileValidationSchema = (showChangePassword) =>
  Yup.object().shape({
    firstName: Yup.string().required('First name is required'),
    // lastName: Yup.string().required('Last name is required'),
    newPassword: showChangePassword
      ? Yup.string().required('New password is required')
      : Yup.string(),
    // .min(6, 'Password must be at least 6 characters')
    confirmPassword: showChangePassword
      ? Yup.string()
          .required('Confirm password is required')
          .oneOf([Yup.ref('newPassword')], 'Passwords must match')
      : Yup.string(),
  });

export const addRegionValidationSchema = Yup.object().shape({
  regionName: Yup.string().required('Region name is required'),
  regionCode: Yup.string().required('Region code is required'),
  tax: Yup.number()
    .typeError('Tax must be a number')
    .required('Tax is required')
    .min(0, 'Tax cannot be negative')
    .max(100, 'Tax cannot exceed 100%'),
  lostCharge: Yup.number()
    .typeError('Lost charge must be a number')
    .required('Lost charge is required')
    .min(0, 'Lost charge cannot be negative'),
  profitShare: Yup.number()
    .typeError('Profit must be required in a number')
    .required('Profit is required')
    .min(0, 'Profit cannot be negative')
    .max(100, 'Profit cannot exceed 100%'),
  settlementDays: Yup.number()
    .typeError('Settlement days must be a number')
    .required('Settlement days is required')
    .min(0, 'Settlement days cannot be negative')
    .integer('Settlement days must be a whole number'),
  orgName: Yup.string().required('Organization name is required'),
  orgAddress: Yup.string().required('Organization address is required'),
  orgCode: Yup.string().required('Organization code is required'),
  landmark: Yup.string().required('Invoice generated is required'),
  eInvoiceFee: Yup.number()
    .typeError('E-invoice fee must be a number')
    .required('E-invoice fee is required')
    .min(0, 'E-invoice fee cannot be negative'),
  timezone: Yup.mixed().required('Timezone is required'),
  currencyOption: Yup.mixed().optional(),
});

export const editRegionLoginUserValidationSchema = (isEditData) => {
  return Yup.object().shape({
    regions: Yup.array().of(
      Yup.object().shape({
        tax: isEditData
          ? Yup.number().nullable()
          : Yup.number()
              .typeError('Tax must be required in a number')
              .required('Tax is required')
              .min(0, 'Tax cannot be negative')
              .max(100, 'Tax cannot exceed 100%'),
        profitShare: isEditData
          ? Yup.number().nullable()
          : Yup.number()
              .typeError('Profit must be required in a number')
              .required('Profit is required')
              .min(0, 'Profit cannot be negative')
              .max(100, 'Profit cannot exceed 100%'),
        settlementDays: isEditData
          ? Yup.number().nullable()
          : Yup.number()
              .typeError('Settlement days must be a number')
              .required('Settlement days is required')
              .min(0, 'Settlement days cannot be negative')
              .integer('Settlement days must be a whole number'),
        orgName: isEditData
          ? Yup.string().nullable()
          : Yup.string().required('Organization name is required'),
        orgAddress: isEditData
          ? Yup.string().nullable()
          : Yup.string().required('Organization address is required'),
        orgCode: isEditData
          ? Yup.string().nullable()
          : Yup.string().required('Organization code is required'),
        landmark: isEditData
          ? Yup.string().nullable()
          : Yup.string().required('Invoice generated is required'),
        eInvoiceFee: isEditData
          ? Yup.number().nullable()
          : Yup.number()
              .typeError('E-invoice fee must be a number')
              .required('E-invoice fee is required')
              .min(0, 'E-invoice fee cannot be negative'),
        timezone: isEditData
          ? Yup.mixed().nullable()
          : Yup.mixed().required('Timezone is required'),
        currencyOption: Yup.mixed().nullable(),
      }),
    ),
  });
};

export const generateInvoiceValidationSchema = Yup.object().shape({
  merchant: Yup.array().of(Yup.mixed().required()),
  year: Yup.mixed().required('Year is required'),
  daysSelect: Yup.mixed().required('Days selection is required'),
  quarter: Yup.mixed()
    .nullable()
    .test(
      'quarter-required-if-daysSelect-is-1',
      'Quarter is required',
      function (value) {
        const { daysSelect } = this.parent;
        if (daysSelect?.id === 1) {
          return !!value?.id;
        }
        return true;
      },
    ),
  month: Yup.mixed()
    .nullable()
    .test(
      'month-required-if-daysSelect-is-2',
      'Month is required',
      function (value) {
        const { daysSelect } = this.parent;
        if (daysSelect?.id === 2) {
          return !!value?.id;
        }
        return true;
      },
    ),
  // RAT-1610: consecutive-month range end. Required in "Months" mode, must not
  // precede the start month, and may span at most 6 consecutive months.
  endMonth: Yup.mixed()
    .nullable()
    .test(
      'endMonth-valid-if-daysSelect-is-2',
      'Select a valid month range (max 6 consecutive months)',
      function (value) {
        const { daysSelect, month } = this.parent;
        if (daysSelect?.id !== 2) {
          return true;
        }
        if (!value?.id) {
          return this.createError({ message: 'To month is required' });
        }
        if (!month?.id) {
          return true;
        }
        if (value.id < month.id) {
          return this.createError({
            message: 'To month cannot be before the from month',
          });
        }
        if (value.id - month.id + 1 > 6) {
          return this.createError({
            message: 'A selection may span at most 6 consecutive months',
          });
        }
        return true;
      },
    ),
});

export const massEjectionValidationSchema = (maxValue) =>
  Yup.object().shape({
    ejectAll: Yup.boolean(),
    count: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        String(originalValue).trim() === '' ? null : value,
      )
      .test('required-if-not-ejectAll', 'Number is required', function (value) {
        const { ejectAll } = this.parent;
        if (!ejectAll) return value !== null && value !== undefined;
        return true; // skip validation if ejectAll is true
      })
      .test('min-value', 'Minimum count is 1', function (value) {
        const { ejectAll } = this.parent;
        if (!ejectAll && value !== undefined) return value >= 1;
        return true;
      })
      .test('max-value', `Maximum count is ${maxValue}`, function (value) {
        const { ejectAll } = this.parent;
        if (!ejectAll && value !== undefined) return value <= maxValue;
        return true;
      }),
  });

export const couponAddEditValidation = Yup.object().shape({
  couponCode: Yup.string()
    .required('Discount code is required')
    .min(3, 'Discount code must be at least 3 characters')
    .max(50, 'Discount code cannot exceed 50 characters')
    .matches(
      /^[A-Z0-9_-]+$/,
      'Discount code can only contain uppercase letters, numbers, hyphens, and underscores',
    ),
  displayText: Yup.string().required('Display Text is required'),
  description: Yup.string(),
  couponType: Yup.string()
    .required('Discount type is required')
    .oneOf(['percentage', 'fixed', 'time'], 'Invalid discount type'),
  couponValue: Yup.number()
    .transform((value) => (value === '' || isNaN(value) ? undefined : value))
    .required('Discount value is required')
    .positive('Discount value must be positive')
    .when('couponType', {
      is: 'percentage',
      then: (schema) => schema.max(100, 'Percentage cannot exceed 100%'),
      otherwise: (schema) =>
        schema.min(0.01, 'Fixed amount must be at least 0.01'),
    }),
  merchantIds: Yup.array().of(
    Yup.number()
      .transform((value) => {
        if (typeof value === 'string') return Number(value);
        if (typeof value === 'object' && value.id) return Number(value.id);
        return value;
      })
      .typeError('Merchant ID must be a number'),
  ),
  startDate: Yup.mixed()
    .nullable()
    .test('is-date', 'Start date must be a valid date', function (value) {
      if (value === '' || value === null || value === undefined) {
        return true;
      }
      return Yup.date().isValidSync(value);
    }),
  endDate: Yup.mixed()
    .nullable()
    .test('is-date', 'End date must be a valid date', function (value) {
      if (value === '' || value === null || value === undefined) {
        return true;
      }
      return Yup.date().isValidSync(value);
    })
    .test(
      'is-after-start',
      'End date must be after start date',
      function (value) {
        const { startDate } = this.parent;
        if (!startDate || !value || startDate === '' || value === '') {
          return true;
        }
        return new Date(value) >= new Date(startDate);
      },
    ),
  regionId: Yup.number()
    .transform((value) => {
      if (typeof value === 'string') return Number(value);
      if (typeof value === 'object' && value.id) return Number(value.id);
      return value;
    })
    .typeError('Region ID must be a number')
    .required('Region is required'),
  isActive: Yup.boolean().default(true),
  maxUsers: Yup.number()
    .transform((value) => (value === '' || isNaN(value) ? undefined : value))
    .positive('Maximum users must be positive')
    .integer('Maximum users must be a whole number'),
  maxUsesPerUser: Yup.number()
    .transform((value) => (value === '' || isNaN(value) ? undefined : value))
    .positive('Maximum uses per user must be positive')
    .integer('Maximum uses per user must be a whole number'),
  maxUsesPerDay: Yup.number()
    .transform((value) => (value === '' || isNaN(value) ? undefined : value))
    .positive('Maximum uses per day must be positive')
    .integer('Maximum uses per day must be a whole number'),
});

export const priceStrategyAddEditValidation = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  regionId: Yup.mixed()
    .nullable()
    .test('is-required-region', 'Region is required', function (value) {
      const { role } = this.options.context || {};
      const isAdmin = role === 1 || role === '1';
      const regionValue = typeof value === 'object' ? value?.id : value;
      if (isAdmin && !regionValue) {
        return this.createError({
          path: this.path,
          message: 'Region is required',
        });
      }
      return true;
    }),
  freeDuration: Yup.number()
    .transform((value) => (value === '' || isNaN(value) ? undefined : value))
    .required('Free Duration is required')
    .positive('Must be a positive number'),
  priceMinute: Yup.number()
    .transform((value) => (value === '' || isNaN(value) ? undefined : value))
    .required('Price per minute is required')
    .positive('Must be a positive number'),
  price: Yup.number()
    .transform((value) => (value === '' || isNaN(value) ? undefined : value))
    .required('Price is required')
    .positive('Must be a positive number')
    .moreThan(0, 'Price must be greater than 0'),
  maxPrice: Yup.number()
    .transform((value) => (value === '' || isNaN(value) ? undefined : value))
    .required('Day capped amount is required')
    .min(0, 'amount must be 0 or greater'),
  deposit: Yup.number()
    .transform((value) => (value === '' || isNaN(value) ? undefined : value))
    .required('Deposit is required')
    .positive('Must be a positive number'),
  overtimeFees: Yup.number()
    .transform((value) => (value === '' || isNaN(value) ? undefined : value))
    .required('Overtime Fees is required'),
  daysLimit: Yup.number()
    .transform((value) => (value === '' || isNaN(value) ? undefined : value))
    .required('Days Limit is required')
    .min(0, 'Days Limit must be 0 or greater'),
});

export const payoutSettingCreateValidation = Yup.object().shape({
  connectedAccount: Yup.object()
    .required('Connected account is required')
    .nullable(),
  revenueSplitRatio: Yup.number()
    .transform((value) => (value === '' || isNaN(value) ? undefined : value))
    .required('Revenue split ratio is required')
    .min(0, 'Must be between 0 and 100')
    .max(100, 'Must be between 0 and 100'),
  payoutFrequency: Yup.object()
    .required('Payout frequency is required')
    .nullable(),
  scheduledDayOfWeek: Yup.object()
    .nullable()
    .when('payoutFrequency', {
      is: (freq) => freq?.id === 'weekly',
      then: (schema) =>
        schema.required('Day of week is required for weekly payout'),
    }),
  scheduledDayOfMonth: Yup.object()
    .nullable()
    .when('payoutFrequency', {
      is: (freq) => freq?.id === 'monthly',
      then: (schema) =>
        schema.required('Day of month is required for monthly payout'),
    }),
  scheduledTime: Yup.string()
    .nullable()
    .when('payoutFrequency', {
      is: (freq) => !!freq?.id,
      then: (schema) =>
        schema
          .required('Settlement time is required')
          .matches(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time must be in HH:mm format'),
    }),
  isActive: Yup.boolean(),
});
